import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { EntitySelect } from '@/components/EntitySelect';
import { InfoHint } from '@/components/ui/info-hint';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormSection } from '@/components/ui/form-section';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import type { ApiError } from '@/core/api';
import { resolveCompanyLabel, searchCompanies } from '@/features/crm/companies/select';
import { CURRENCIES } from '@/features/crm/deals/constants';
import { resolveDealLabel, searchDeals } from '@/features/crm/deals/select';
import { resolveProjectLabel, searchProjects } from '@/features/projects/select';
import { CONTRACT_STATUSES } from '../constants';
import type { ContractInput } from '../types';

const schema = z.object({
  title: z.string().trim().min(1, 'Title is required').max(255),
  company: z.string().min(1, 'Company is required'),
  deal: z.string(),
  project: z.string(),
  status: z.enum(['draft', 'sent', 'signed', 'cancelled']),
  currency: z.enum(['USD', 'EUR', 'GBP', 'AUD']),
  amount: z
    .string()
    .trim()
    .refine((v) => v === '' || Number(v) >= 0, 'Amount must be a positive number'),
  starts_on: z.string(),
  ends_on: z.string(),
  notes: z.string(),
});

type FormValues = z.infer<typeof schema>;

const FIELD_KEYS = [
  'title', 'company', 'deal', 'status', 'currency', 'amount', 'starts_on', 'ends_on', 'notes',
] as const;

interface ContractFormProps {
  defaultValues?: Partial<FormValues>;
  submitLabel: string;
  onSubmit: (values: ContractInput) => Promise<unknown>;
  onCancel: () => void;
}

export function ContractForm({ defaultValues, submitLabel, onSubmit, onCancel }: ContractFormProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: '',
      company: '',
      deal: '',
      project: '',
      status: 'draft',
      currency: 'USD',
      amount: '',
      starts_on: '',
      ends_on: '',
      notes: '',
      ...defaultValues,
    },
  });

  async function handleSubmit(values: FormValues) {
    const amount = values.amount === '' ? '0' : values.amount;
    const input: ContractInput = {
      title: values.title,
      company: Number(values.company),
      deal: values.deal ? Number(values.deal) : null,
      project: values.project ? Number(values.project) : null,
      // `proposal` is deliberately NOT sent: the form has no field for it, and
      // PATCH is partial — sending null severed the Proposal → Contract spine
      // link on every edit of a proposal-born contract.
      status: values.status,
      currency: values.currency,
      amount,
      // Manual contracts are entered in their own currency; we snapshot the
      // base equal to the amount (precise FX snapshots come from the
      // Proposal → Contract path). USD contracts make base == amount anyway.
      base_amount: amount,
      starts_on: values.starts_on || null,
      ends_on: values.ends_on || null,
      notes: values.notes,
    };
    try {
      await onSubmit(input);
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          const key = FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
    }
  }

  const submitting = form.formState.isSubmitting;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="max-w-2xl space-y-6">
        <FormSection variant="card" title="Contract" description="What's being agreed?">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Title</FormLabel>
                <FormControl>
                  <Input placeholder="Master Services Agreement — Acme" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        <FormSection variant="card" title="Status & value" description="Where does it stand, and for how much?">
          <div className="grid gap-4 sm:grid-cols-3">
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {CONTRACT_STATUSES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="currency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Currency</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {CURRENCIES.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount</FormLabel>
                  <FormControl>
                    <Input type="number" min="0" step="0.01" placeholder="0.00" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        <FormSection variant="card" title="Term" description="Optional start / end dates.">
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="starts_on"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Starts on</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="ends_on"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ends on</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        <FormSection variant="card" title="Relations" description="Who's it for?">
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="company"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company</FormLabel>
                  <FormControl>
                    <EntitySelect
                      queryKey="company-picker"
                      value={field.value}
                      onChange={field.onChange}
                      search={searchCompanies}
                      resolveLabel={resolveCompanyLabel}
                      placeholder="Select a company"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="deal"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Deal</FormLabel>
                  <FormControl>
                    <EntitySelect
                      queryKey="deal-picker"
                      value={field.value}
                      onChange={field.onChange}
                      search={searchDeals}
                      resolveLabel={resolveDealLabel}
                      placeholder="No deal"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="project"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1.5">
                    Project
                    <InfoHint text="Delivery project for this contract. Assign an existing one when the project already exists — signing the contract then reuses it instead of creating another." />
                  </FormLabel>
                  <FormControl>
                    <EntitySelect
                      queryKey="contract-project-picker"
                      value={field.value}
                      onChange={field.onChange}
                      search={searchProjects}
                      resolveLabel={resolveProjectLabel}
                      placeholder="No project"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        <FormSection variant="card" title="Notes" description="Anything extra to capture?">
          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Notes</FormLabel>
                <FormControl>
                  <Textarea rows={3} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        {form.formState.errors.root ? (
          <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
        ) : null}

        <div className="flex gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : submitLabel}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  );
}
